import { Money } from "@redotech/money";
import { MarketingChannel } from "@redotech/redo-billing-common/marketing-channel";
import type { MoneyV2 } from "@redotech/redo-common-model/common/shopify-pixel-types";
import { ConversationStatus } from "@redotech/redo-common-model/support/conversation-status";
import {
  CheckoutConversionMetricInformationStatus,
  CheckoutConversionMetricSource,
} from "@redotech/redo-conversion-metrics-common/checkout-conversion-metrics";
import {
  LoyaltyEarningRuleType,
  LoyaltyPointAdjustmentReason,
  LoyaltyPointAdjustmentType,
} from "@redotech/redo-loyalty-common/loyalty-definition";
import { SubscriptionDelayOption } from "@redotech/redo-marketing-email-builder-common/subscription-utils";
import { OutreachGroupKey } from "@redotech/redo-marketing-outreach-common/outreach";
import { ShopifyAddress } from "@redotech/redo-order-common/shopify-address";
import { ReturnStatus } from "@redotech/redo-return-core-common/return-status";
import { YyyyMmDd } from "@redotech/zod-util/db-coercion";
import {
  BaseCustomerEvent,
  CheckoutRatesShownCustomerEvent,
  CustomEventEvent,
  CustomerEventType,
  ExchangeProcessedWithCreditEvent,
  ExternalCustomerPlatform,
  FailedDiscountDetectedEvent,
  FailedDiscountRescueClaimedEvent,
  FailedDiscountRescueShownEvent,
  MarketingConfirmEvent,
  MarketingSubscribeEvent,
  MarketingUnsubscribeEvent,
  MeaningfulRecoverConversationEvent,
  OrderCreatedCustomerEvent,
  OrderStatusUpdatedCustomerEvent,
  OrderUpdatedCustomerEvent,
  PersonaAssignmentEvent,
  PostPurchaseOfferAcceptedEvent,
  PostPurchasePageViewedEvent,
  PriceDropInterestInferredEvent,
  ProductSubscriptionActionType,
  ProductSubscriptionCancelledCustomerEvent,
  ProductSubscriptionCreatedEvent,
  RefundReturnSubmittedEvent,
  RescuePanelCtaClickedEvent,
  RescuePanelViewedEvent,
  ReviewCreatedEvent,
  ShopperAgentBackInStockSubscribedEvent,
  ShopperAgentDiscountCodeGeneratedEvent,
  ShopperAgentGiftCardResentEvent,
  ShopperAgentKnowledgeRetrievedEvent,
  ShopperAgentLinkFollowedEvent,
  ShopperAgentOrderCancelledEvent,
  ShopperAgentOrderItemsUpdatedEvent,
  ShopperAgentOrderTrackingProvidedEvent,
  ShopperAgentProductsSearchedEvent,
  ShopperAgentReturnCreatedEvent,
  ShopperAgentReturnLabelResentEvent,
  ShopperAgentShippingAddressUpdatedEvent,
  SignupFormSubmittedEvent,
  SignupFormViewedEvent,
  SubscribedToBackInStockEvent,
  TrackingPageClickedEvent,
  TrackingPageViewedEvent,
  TriggeredBackInStockNotificationEvent,
  WarrantyRegisteredEvent,
} from "./customer-event-zod-schema";
import { AutomationExitReason } from "./events/base";
import {
  InboundReceivedEvent,
  OutreachBounceEvent,
  OutreachClickEvent,
  OutreachComplaintEvent,
  OutreachDeliveryEvent,
  OutreachFailedEvent,
  OutreachOpenEvent,
  OutreachSendEvent,
} from "./events/outreach";
import {
  CheckoutCompletedShoppingEvent,
  CheckoutContactInfoSubmittedShoppingEvent,
  CheckoutStartedShoppingEvent,
  CollectionViewedShoppingEvent,
  PaymentFailedShoppingEvent,
  ProductAddedToCartShoppingEvent,
  ProductRemovedFromCartShoppingEvent,
  ProductViewedShoppingEvent,
  StorePageViewedShoppingEvent,
} from "./events/pixel";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export {
  BounceType,
  CUSTOMER_EVENT_DISCRIMINATOR_KEY,
  CustomerEventType,
  ExternalCustomerPlatform,
  ImportSource,
  MarketingConfirmSource,
  PaymentFailureStage,
  ProductSubscriptionActionType,
  ReputationImpact,
  UnsubscribeSource,
} from "./customer-event-zod-schema";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export type {
  BaseCustomerEvent,
  CheckoutRatesShownCustomerEvent,
  CustomEventEvent,
  ExchangeProcessedWithCreditEvent,
  FailedDiscountDetectedEvent,
  FailedDiscountRescueClaimedEvent,
  FailedDiscountRescueShownEvent,
  MarketingConfirmEvent,
  MarketingEvent,
  MarketingSubscribeEvent,
  MarketingUnsubscribeEvent,
  MeaningfulRecoverConversationEvent,
  OrderCreatedCustomerEvent,
  OrderStatusUpdatedCustomerEvent,
  OrderUpdatedCustomerEvent,
  PersonaAssignmentEvent,
  PostPurchaseOfferAcceptedEvent,
  PostPurchasePageViewedEvent,
  PriceDropInterestInferredEvent,
  ProductSubscriptionCancelledCustomerEvent,
  ProductSubscriptionCreatedEvent,
  RefundReturnSubmittedEvent,
  ReviewCreatedEvent,
  ShopperAgentBackInStockSubscribedEvent,
  ShopperAgentDiscountCodeGeneratedEvent,
  ShopperAgentGiftCardResentEvent,
  ShopperAgentKnowledgeRetrievedEvent,
  ShopperAgentLinkFollowedEvent,
  ShopperAgentOrderCancelledEvent,
  ShopperAgentOrderItemsUpdatedEvent,
  ShopperAgentOrderTrackingProvidedEvent,
  ShopperAgentProductsSearchedEvent,
  ShopperAgentReturnCreatedEvent,
  ShopperAgentReturnLabelResentEvent,
  ShopperAgentShippingAddressUpdatedEvent,
  SubscribedToBackInStockEvent,
  TrackingPageClickedEvent,
  TrackingPageViewedEvent,
  TriggeredBackInStockNotificationEvent,
  WarrantyRegisteredEvent,
} from "./customer-event-zod-schema";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export type {
  EmailOutreachBaseEvent,
  EmailOutreachBounceEvent,
  EmailOutreachClickEvent,
  EmailOutreachComplaintEvent,
  EmailOutreachDeliveryEvent,
  EmailOutreachOpenEvent,
  EmailOutreachSendEvent,
  InboundReceivedEvent,
  OutreachBounceEvent,
  OutreachClickEvent,
  OutreachComplaintEvent,
  OutreachDeliveryEvent,
  OutreachFailedEvent,
  OutreachOpenEvent,
  OutreachSendEvent,
  SmsOutreachBaseEvent,
  SmsOutreachDeliveryEvent,
  SmsOutreachFailedEvent,
  SmsOutreachSendEvent,
} from "./events/outreach";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export type {
  BaseShoppingEvent,
  CheckoutCompletedShoppingEvent,
  CheckoutContactInfoSubmittedShoppingEvent,
  CheckoutStartedShoppingEvent,
  CollectionViewedShoppingEvent,
  PaymentFailedShoppingEvent,
  ProductAddedToCartShoppingEvent,
  ProductRemovedFromCartShoppingEvent,
  ProductViewedShoppingEvent,
  StorePageViewedShoppingEvent,
} from "./events/pixel";

// DON'T ADD ANY MORE TYPESCRIPT INTERFACES HERE.
// Add them to the customer-event-zod-schema.ts file instead and just import the z.infer'd types here

// ###
// OrderCustomerEvents
// ###
export type OrderCustomerEvent =
  | OrderCreatedCustomerEvent
  | OrderUpdatedCustomerEvent
  | OrderStatusUpdatedCustomerEvent;

// ###
// ReturnCustomerEvents
// ###
export interface IReturnCustomerEvent extends BaseCustomerEvent {
  returnId: string;
}

export interface ReturnCreatedCustomerEvent extends IReturnCustomerEvent {
  eventType: CustomerEventType.RETURN_CREATED;
}

export interface ReturnStatusUpdatedCustomerEvent extends IReturnCustomerEvent {
  eventType: CustomerEventType.RETURN_STATUS_UPDATED;
  newStatus: ReturnStatus;
}

export interface ReturnCommentCreatedCustomerEvent extends IReturnCustomerEvent {
  eventType: CustomerEventType.RETURN_COMMENT_CREATED;
  messageId: string;
}

export interface ReturnProcessedCustomerEvent extends IReturnCustomerEvent {
  eventType: CustomerEventType.RETURN_PROCESSED;
}

export type ReturnCustomerEvent =
  | ReturnCreatedCustomerEvent
  | ReturnStatusUpdatedCustomerEvent
  | ReturnCommentCreatedCustomerEvent
  | ReturnProcessedCustomerEvent;

export interface ReturnProcessedCustomerEvent extends IReturnCustomerEvent {
  eventType: CustomerEventType.RETURN_PROCESSED;
}

// ###
// ClaimCustomerEvents
// ###
export interface IClaimCustomerEvent extends BaseCustomerEvent {
  claimId: string;
}

export interface ClaimCreatedCustomerEvent extends IClaimCustomerEvent {
  eventType: CustomerEventType.CLAIM_CREATED;
}

export interface ClaimStatusUpdatedCustomerEvent extends IClaimCustomerEvent {
  eventType: CustomerEventType.CLAIM_STATUS_UPDATED;
  newStatus: ReturnStatus;
}

export interface ClaimCommentCreatedCustomerEvent extends IClaimCustomerEvent {
  eventType: CustomerEventType.CLAIM_COMMENT_CREATED;
  messageId: string;
}

// ###
// ConversationCustomerEvents
// ###
export interface IConversationCustomerEvent extends BaseCustomerEvent {
  conversationId: string;
}

export interface ConversationCreatedCustomerEvent extends IConversationCustomerEvent {
  eventType: CustomerEventType.CONVERSATION_CREATED;
}

export interface ConversationStatusUpdatedCustomerEvent extends IConversationCustomerEvent {
  eventType: CustomerEventType.CONVERSATION_STATUS_UPDATED;
  newStatus: ConversationStatus;
}

export interface ConversationMessageCreatedCustomerEvent extends IConversationCustomerEvent {
  eventType: CustomerEventType.CONVERSATION_MESSAGE_CREATED;
  messageId: string;
}

export interface ProductVisualizationGeneratedEvent extends IConversationCustomerEvent {
  eventType: CustomerEventType.PRODUCT_VISUALIZATION_GENERATED;
  productId: string;
  variantId?: string;
  visualizationUrl: string;
}

// ###
// ExternalPlatformCustomerCreatedEvent
// ###
export interface ExternalPlatformCustomerCreatedEvent extends BaseCustomerEvent {
  eventType: CustomerEventType.EXTERNAL_PLATFORM_CUSTOMER_CREATED;
  externalPlatform: ExternalCustomerPlatform;
  externalPlatformCustomerId: string;
}

// ###
// StorefrontLoginEvent
// ###
export interface StorefrontLoginEvent extends BaseCustomerEvent {
  eventType: CustomerEventType.STOREFRONT_LOGIN;
  multipassLogin: boolean;
}

export interface ProductInfo {
  productId: string;
  productTitle: string;
  variantId?: string;
  variantTitle?: string;
  url?: string;
  price: MoneyV2;
  priceRange?: { minVariantPrice: MoneyV2; maxVariantPrice: MoneyV2 };
  imageUrl?: string;
  handle?: string;
}

export interface CheckoutShippingOption {
  code: string;
  /**
   * The original service code visible to Shopify before metafield substitution.
   * Only present when the code was resolved from a shipping option metafield
   * instead of the service code directly (i.e., when `useShippingOptionMetafields` is enabled).
   */
  originalServiceCode?: string | undefined;
  cost: Money;
  rateTableId?: string | undefined;
  promisedDeliveryDate?:
    | { datePromised: string; deliveryDays: number }
    | undefined;
  mercuryModelId?: string | undefined;
}

export interface CheckoutInfoChangedShoppingEvent extends BaseCustomerEvent {
  eventType: CustomerEventType.CHECKOUT_INFO_CHANGED;
  checkoutToken: string;
  subtotalPrice: Money;
  itemQuantity: number;
  customerOrderCount:
    | { status: CheckoutConversionMetricInformationStatus.KNOWN; value: number }
    | { status: CheckoutConversionMetricInformationStatus.UNKNOWN };
  treatmentId:
    | {
        status: CheckoutConversionMetricInformationStatus.KNOWN;
        value?: string;
      }
    | { status: CheckoutConversionMetricInformationStatus.UNKNOWN };
  localDateTime: {
    isoDateTime: Date;
    timeZone: string;
    localDayOfWeek: "sun" | "mon" | "tue" | "wed" | "thu" | "fri" | "sat";
    localTimeInSecondsSinceMidnight: number;
  };
  location?: { countryCode?: string; provinceCode?: string };
  shippingAddress?: {
    address1?: string;
    address2?: string;
    city?: string;
    provinceCode?: string;
    countryCode?: string;
    zip?: string;
  };
  metricSource?: CheckoutConversionMetricSource;
  deliveryOptionsByGroup?: {
    deliveryOptions: CheckoutShippingOption[];
    mercury?: { combinationsExplored: number };
    checkoutExperienceId?: string;
  }[];
  stagecoachPackageProtectionPriceDollars?: number;
  stagecoachReturnsCoveragePriceDollars?: number;
  stagecoachCartValueDollars?: number;
  stagecoachModelVersionId?: string;
  stagecoachBetaSampleIndex?: number;
}

export interface CheckoutProbabilityCalculatedEvent extends BaseCustomerEvent {
  eventType: CustomerEventType.CHECKOUT_PROBABILITY_CALCULATED;
  sessionId: string;
  instantaneousHazardProbability: number;
  cumulativeConversionProbability: number;
  sessionAge: number;
  totalBuckets: number;
}

export interface PackageProtectionPriceExposedEvent extends BaseCustomerEvent {
  eventType: CustomerEventType.PACKAGE_PROTECTION_PRICE_EXPOSED;
  anonymousId: string;
  treatmentId?: string;
  isStagecoachArm: boolean;
  modelVersionId?: string;
  betaSampleIndex?: number;
  packageProtectionPriceDollars?: number;
  returnsCoveragePriceDollars?: number;
  cartValue: number;
  cartItemCount: number;
  cartHash: string;
  checkoutProbability?: number;
}

// TODO: remove all of the outreach interfaces from this file, and export the z.infer equivalents from ./events/outreach.ts
export interface OutreachBaseEvent extends BaseCustomerEvent, OutreachGroupKey {
  template?: string;
  category?: string;
  channelId: string;
  channel: MarketingChannel;
  /**
   * The email address or phone number that the outreach was sent from.
   * Currently only populated for SES-based email events, not AMP emails or SMS,
   * therefore it should not be considered reliable.
   */
  fromChannelId?: string;
  recipientId?: string;
  orderId?: string;
  returnId?: string;
  externalMessageId?: string;
  templateName?: string;
  discountCodes?: string[] | null;
}

interface CustomerGroupMembershipEvent extends BaseCustomerEvent {
  customerGroupId: string;
}

export interface CustomerGroupEnterEvent extends CustomerGroupMembershipEvent {
  eventType: CustomerEventType.CUSTOMER_GROUP_ENTER;
}

export interface CustomerGroupExitEvent extends CustomerGroupMembershipEvent {
  eventType: CustomerEventType.CUSTOMER_GROUP_EXIT;
}

interface AutomationLifecycleEvent extends BaseCustomerEvent {
  automationId: string;
}

interface ExecutedAutomationLifecycleEvent extends AutomationLifecycleEvent {
  /** Pairs an entry with its exit without relying on relative timestamps. */
  automationExecutionId: string;
}

export interface AutomationEnteredEvent extends ExecutedAutomationLifecycleEvent {
  eventType: CustomerEventType.AUTOMATION_ENTERED;
}

export interface AutomationExitedEvent extends ExecutedAutomationLifecycleEvent {
  eventType: CustomerEventType.AUTOMATION_EXITED;
  exitReason: AutomationExitReason;
}

/** Skipped at entry, before a run exists — hence no execution id. */
export interface AutomationSkippedEvent extends AutomationLifecycleEvent {
  eventType: CustomerEventType.AUTOMATION_SKIPPED;
}

export type AutomationLifecycleCustomerEvent =
  | AutomationEnteredEvent
  | AutomationExitedEvent
  | AutomationSkippedEvent;

export function isAutomationLifecycleEvent(
  event: CustomerEvent,
): event is AutomationLifecycleCustomerEvent {
  return [
    CustomerEventType.AUTOMATION_ENTERED,
    CustomerEventType.AUTOMATION_EXITED,
    CustomerEventType.AUTOMATION_SKIPPED,
  ].includes(event.eventType);
}
interface LoyaltyPointsBaseEvent extends BaseCustomerEvent {
  customer: string;
  finalPointAmount: number;
}

export interface LoyaltyPointsRedeemEvent extends LoyaltyPointsBaseEvent {
  eventType: CustomerEventType.LOYALTY_REDEEM;
  orderId: string;
  discountCodes?: string[] | null;
}

export interface LoyaltyPointsReturnRefundEvent extends LoyaltyPointsBaseEvent {
  eventType: CustomerEventType.LOYALTY_RETURN_REFUND;
  returnId: string;
}

export interface LoyaltyPointsReturnClawbackEvent extends LoyaltyPointsBaseEvent {
  eventType: CustomerEventType.LOYALTY_RETURN_CLAWBACK;
  returnId: string;
}

export interface LoyaltyPointsCancelPurchaseRefundEvent extends LoyaltyPointsBaseEvent {
  eventType: CustomerEventType.LOYALTY_CANCEL_PURCHASE_REFUND;
  orderId: string;
}

export interface LoyaltyPointsCancelPurchaseClawbackEvent extends LoyaltyPointsBaseEvent {
  eventType: CustomerEventType.LOYALTY_CANCEL_PURCHASE_CLAWBACK;
  orderId: string;
}

export interface LoyaltyPointsManualAdjustmentEvent extends LoyaltyPointsBaseEvent {
  eventType: CustomerEventType.LOYALTY_MANUAL_ADJUSTMENT;
  reason: string;
}

export interface LoyaltyPointsExpireEvent extends LoyaltyPointsBaseEvent {
  eventType: CustomerEventType.LOYALTY_EXPIRE;
}

interface LoyaltyPointsBaseEarnEvent extends LoyaltyPointsBaseEvent {
  earningRuleType: LoyaltyEarningRuleType;
  basePointAmount: number;
  pointAdjustments: {
    adjustmentReason: LoyaltyPointAdjustmentReason;
    adjustmentType: LoyaltyPointAdjustmentType;
    adjustmentAmount: number;
  }[];
  expirationDate: Date;
  appliedPromotionsIds: string[];
}

export interface LoyaltyPointsEarnEvent extends LoyaltyPointsBaseEarnEvent {
  eventType: CustomerEventType.LOYALTY_EARN;
}

export interface LoyaltyPointsEarnReviewEvent extends LoyaltyPointsBaseEarnEvent {
  eventType: CustomerEventType.LOYALTY_EARN_REVIEW;
  earningRuleType: LoyaltyEarningRuleType.REVIEW;
  reviewId: string;
}

export interface LoyaltyPointsEarnPurchaseEvent extends LoyaltyPointsBaseEarnEvent {
  eventType: CustomerEventType.LOYALTY_EARN_PURCHASE;
  earningRuleType: LoyaltyEarningRuleType.PURCHASE;
  orderId: string;
  shopifyOrderLineItemId: string;
}

export type LoyaltyPointsEarningEvent =
  | LoyaltyPointsEarnEvent
  | LoyaltyPointsEarnReviewEvent
  | LoyaltyPointsEarnPurchaseEvent;

export type LoyaltyPointsEvent =
  | LoyaltyPointsRedeemEvent
  | LoyaltyPointsReturnRefundEvent
  | LoyaltyPointsReturnClawbackEvent
  | LoyaltyPointsCancelPurchaseRefundEvent
  | LoyaltyPointsCancelPurchaseClawbackEvent
  | LoyaltyPointsManualAdjustmentEvent
  | LoyaltyPointsExpireEvent
  | LoyaltyPointsEarningEvent;

export function isLoyaltyPointsEvent(
  event: CustomerEvent,
): event is LoyaltyPointsEvent {
  return [
    CustomerEventType.LOYALTY_REDEEM,
    CustomerEventType.LOYALTY_RETURN_REFUND,
    CustomerEventType.LOYALTY_RETURN_CLAWBACK,
    CustomerEventType.LOYALTY_CANCEL_PURCHASE_REFUND,
    CustomerEventType.LOYALTY_CANCEL_PURCHASE_CLAWBACK,
    CustomerEventType.LOYALTY_MANUAL_ADJUSTMENT,
    CustomerEventType.LOYALTY_EXPIRE,
    CustomerEventType.LOYALTY_EARN,
    CustomerEventType.LOYALTY_EARN_REVIEW,
    CustomerEventType.LOYALTY_EARN_PURCHASE,
  ].includes(event.eventType);
}

export function isLoyaltyPointsEarnEvent(
  event: CustomerEvent,
): event is LoyaltyPointsEarnEvent {
  return [
    CustomerEventType.LOYALTY_EARN,
    CustomerEventType.LOYALTY_EARN_REVIEW,
    CustomerEventType.LOYALTY_EARN_PURCHASE,
  ].includes(event.eventType);
}

export type CustomerGroupEvent =
  | CustomerGroupEnterEvent
  | CustomerGroupExitEvent;

export function isCustomerGroupEvent(
  event: CustomerEvent,
): event is CustomerGroupEvent {
  return [
    CustomerEventType.CUSTOMER_GROUP_ENTER,
    CustomerEventType.CUSTOMER_GROUP_EXIT,
  ].includes(event.eventType);
}

export interface CustomerInformationUpdatedEvent extends BaseCustomerEvent {
  eventType: CustomerEventType.CUSTOMER_INFORMATION_UPDATE;
  newPhoneNumber?: string;
  newFirstName?: string;
  newLastName?: string;
}

export interface OmsOrderReadyForPickupEvent extends BaseCustomerEvent {
  eventType: CustomerEventType.OMS_ORDER_READY_FOR_PICKUP;
  fulfillmentGroupId: string;
  fulfillmentOrderId?: string | null | undefined; // Unused now, only left for backwards compat
}

export interface AddressVerificationFailedEvent extends BaseCustomerEvent {
  eventType: CustomerEventType.ADDRESS_VERIFICATION_FAILED;
  fulfillmentGroupId: string;
  originalAddress: ShopifyAddress;
  errorMessages: string[];
  email: string; // needs to be required in this case because we don't have a customer object
  merchantName: string;
}

// Recharge gives us schedules as plain date strings with no time zone

export interface BaseProductSubscriptionActionEvent extends BaseCustomerEvent {
  eventType: CustomerEventType.PRODUCT_SUBSCRIPTION_ACTION;
  subscriptionIds: string[];
  emailId: string;
  nextChargeDate: { original: YyyyMmDd; new?: YyyyMmDd | null };
  /** The specific charge ID this action should apply to (prevents actions on wrong charge) */
  chargeId: string;
}

export interface ProductSubscriptionDelayedEvent extends BaseProductSubscriptionActionEvent {
  actionType: ProductSubscriptionActionType.DELAY;
  delayOption: SubscriptionDelayOption;
}

export interface ProductSubscriptionSkippedEvent extends BaseProductSubscriptionActionEvent {
  actionType: ProductSubscriptionActionType.SKIP;
}

export type ProductSubscriptionActionEvent =
  | ProductSubscriptionDelayedEvent
  | ProductSubscriptionSkippedEvent;

export type OutreachEvent =
  | OutreachSendEvent
  | OutreachDeliveryEvent
  | OutreachOpenEvent
  | OutreachClickEvent
  | OutreachBounceEvent
  | OutreachComplaintEvent
  | OutreachFailedEvent;

export const outreachEventTypes = [
  CustomerEventType.OUTREACH_SEND,
  CustomerEventType.OUTREACH_DELIVERY,
  CustomerEventType.OUTREACH_OPEN,
  CustomerEventType.OUTREACH_CLICK,
  CustomerEventType.OUTREACH_BOUNCE,
  CustomerEventType.OUTREACH_COMPLAINT,
] as const;
export const OutreachEvent = outreachEventTypes;

export function isOutreachEvent(event: CustomerEvent): event is OutreachEvent {
  return [
    CustomerEventType.OUTREACH_SEND,
    CustomerEventType.OUTREACH_DELIVERY,
    CustomerEventType.OUTREACH_OPEN,
    CustomerEventType.OUTREACH_CLICK,
    CustomerEventType.OUTREACH_BOUNCE,
    CustomerEventType.OUTREACH_COMPLAINT,
    CustomerEventType.OUTREACH_FAILED,
  ].includes(event.eventType);
}

export function isCustomEvent(event: CustomerEvent): event is CustomEventEvent {
  return event.eventType === CustomerEventType.CUSTOM_EVENT;
}

export type ShopperAgentCustomerEvent =
  | ShopperAgentReturnCreatedEvent
  | ShopperAgentOrderCancelledEvent
  | ShopperAgentOrderItemsUpdatedEvent
  | ShopperAgentShippingAddressUpdatedEvent
  | ShopperAgentReturnLabelResentEvent
  | ShopperAgentGiftCardResentEvent
  | ShopperAgentDiscountCodeGeneratedEvent
  | ShopperAgentKnowledgeRetrievedEvent
  | ShopperAgentOrderTrackingProvidedEvent
  | ShopperAgentBackInStockSubscribedEvent
  | ShopperAgentProductsSearchedEvent
  | ShopperAgentLinkFollowedEvent;

export function isShopperAgentCustomerEvent(
  event: CustomerEvent,
): event is ShopperAgentCustomerEvent {
  return [
    CustomerEventType.SHOPPER_AGENT_RETURN_CREATED,
    CustomerEventType.SHOPPER_AGENT_ORDER_CANCELLED,
    CustomerEventType.SHOPPER_AGENT_ORDER_ITEMS_UPDATED,
    CustomerEventType.SHOPPER_AGENT_SHIPPING_ADDRESS_UPDATED,
    CustomerEventType.SHOPPER_AGENT_RETURN_LABEL_RESENT,
    CustomerEventType.SHOPPER_AGENT_GIFT_CARD_RESENT,
    CustomerEventType.SHOPPER_AGENT_DISCOUNT_CODE_GENERATED,
    CustomerEventType.SHOPPER_AGENT_KNOWLEDGE_RETRIEVED,
    CustomerEventType.SHOPPER_AGENT_ORDER_TRACKING_PROVIDED,
    CustomerEventType.SHOPPER_AGENT_BACK_IN_STOCK_SUBSCRIBED,
    CustomerEventType.SHOPPER_AGENT_PRODUCTS_SEARCHED,
    CustomerEventType.SHOPPER_AGENT_LINK_FOLLOWED,
  ].includes(event.eventType as CustomerEventType);
}

export type CustomerEvent =
  | OrderCreatedCustomerEvent
  | OrderUpdatedCustomerEvent
  | OrderStatusUpdatedCustomerEvent
  | ReturnCreatedCustomerEvent
  | ReturnStatusUpdatedCustomerEvent
  | ReturnCommentCreatedCustomerEvent
  | ReturnProcessedCustomerEvent
  | ClaimCreatedCustomerEvent
  | ClaimStatusUpdatedCustomerEvent
  | ClaimCommentCreatedCustomerEvent
  | ConversationCreatedCustomerEvent
  | ConversationStatusUpdatedCustomerEvent
  | ConversationMessageCreatedCustomerEvent
  | ProductVisualizationGeneratedEvent
  | ExternalPlatformCustomerCreatedEvent
  | StorefrontLoginEvent
  | MarketingSubscribeEvent
  | MarketingConfirmEvent
  | MarketingUnsubscribeEvent
  | ProductViewedShoppingEvent
  | ProductAddedToCartShoppingEvent
  | PaymentFailedShoppingEvent
  | OutreachSendEvent
  | OutreachDeliveryEvent
  | OutreachOpenEvent
  | OutreachClickEvent
  | OutreachBounceEvent
  | OutreachComplaintEvent
  | OutreachFailedEvent
  | InboundReceivedEvent
  | CheckoutStartedShoppingEvent
  | CheckoutInfoChangedShoppingEvent
  | CheckoutRatesShownCustomerEvent
  | CheckoutCompletedShoppingEvent
  | ProductRemovedFromCartShoppingEvent
  | CheckoutContactInfoSubmittedShoppingEvent
  | StorePageViewedShoppingEvent
  | CollectionViewedShoppingEvent
  | CustomerGroupEnterEvent
  | CustomerGroupExitEvent
  | AutomationEnteredEvent
  | AutomationExitedEvent
  | AutomationSkippedEvent
  | CustomerInformationUpdatedEvent
  | OmsOrderReadyForPickupEvent
  | AddressVerificationFailedEvent
  | ProductSubscriptionActionEvent
  | LoyaltyPointsEvent
  | SubscribedToBackInStockEvent
  | TriggeredBackInStockNotificationEvent
  | PersonaAssignmentEvent
  | SignupFormViewedEvent
  | SignupFormSubmittedEvent
  | RescuePanelViewedEvent
  | RescuePanelCtaClickedEvent
  | FailedDiscountDetectedEvent
  | FailedDiscountRescueShownEvent
  | FailedDiscountRescueClaimedEvent
  | CheckoutProbabilityCalculatedEvent
  | PackageProtectionPriceExposedEvent
  | MeaningfulRecoverConversationEvent
  | ShopperAgentReturnCreatedEvent
  | ShopperAgentOrderCancelledEvent
  | ShopperAgentOrderItemsUpdatedEvent
  | ShopperAgentShippingAddressUpdatedEvent
  | ShopperAgentReturnLabelResentEvent
  | ShopperAgentGiftCardResentEvent
  | ShopperAgentDiscountCodeGeneratedEvent
  | ShopperAgentKnowledgeRetrievedEvent
  | ShopperAgentOrderTrackingProvidedEvent
  | ShopperAgentBackInStockSubscribedEvent
  | ShopperAgentProductsSearchedEvent
  | ShopperAgentLinkFollowedEvent
  | ProductSubscriptionCreatedEvent
  | ProductSubscriptionCancelledCustomerEvent
  | PriceDropInterestInferredEvent
  | RefundReturnSubmittedEvent
  | ExchangeProcessedWithCreditEvent
  | WarrantyRegisteredEvent
  | PostPurchasePageViewedEvent
  | PostPurchaseOfferAcceptedEvent
  | TrackingPageViewedEvent
  | TrackingPageClickedEvent
  | ReviewCreatedEvent
  | CustomEventEvent;

export type Create<T> = T extends OrderStatusUpdatedCustomerEvent
  ? Omit<T, "_id" | "timestamp" | "newStatus"> & {
      newStatus: string;
      timestamp?: Date;
    }
  : T extends BaseProductSubscriptionActionEvent
    ? Omit<T, "_id" | "timestamp" | "nextChargeDate">
    : Omit<T, "_id" | "timestamp"> & { _id?: string; timestamp?: Date };
